import { useCallback, useEffect, useRef } from 'react';
import { useEsriMapContext } from '../EsriMapContext';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import EsriUtils from '../utils/esriHelpers';
import {
	extractLngLatPositions,
	type ValidGeometry,
} from '@Map/extractLngLatPositions';
import { convertCoordsToBounds } from '@Map/utils';

const DEFAULT_DROP_PIN_LAYER_ID = 'search-drop-pin';

/** One map pin center per geometry (point = coordinates; else bbox center). */
export function centersForDropPinsFromGeometries(
	geometries: GeoJSON.Geometry[],
): [number, number][] {
	const valid = geometries.filter((g): g is ValidGeometry => {
		return !!g && typeof g === 'object' && 'coordinates' in g;
	});
	return valid.map(g => {
		if (g.type === 'Point') {
			const center = EsriUtils.lngLatToCenter({
				lng: g.coordinates[0],
				lat: g.coordinates[1],
			});
			return center as [number, number];
		}
		const coords = extractLngLatPositions([g]);
		const bounds = convertCoordsToBounds(coords);
		const center = EsriUtils.lngLatToPoint(bounds.getCenter());
		return [center.longitude, center.latitude] as [number, number];
	});
}

const makeTeardropSvg = (color: string) =>
	`data:image/svg+xml;charset=utf-8,${encodeURIComponent(
		'<svg xmlns="http://www.w3.org/2000/svg" width="27" height="41" viewBox="0 0 27 41">' +
			`<path fill="${color}" d="M13.5 0C6.044 0 0 6.044 0 13.5c0 9.188 13.5 27.5 13.5 27.5S27 22.688 27 13.5C27 6.044 20.956 0 13.5 0z"/>` +
			'<circle fill="white" cx="13.5" cy="13.5" r="5.5"/>' +
			'</svg>',
	)}`;

// Search result pins (#4668f2) and geocoder drop pins (#768d95, matching Mapbox DropPin).
const DEFAULT_PIN_COLOR = '#4668f2';
const DEFAULT_PIN_SVG = makeTeardropSvg(DEFAULT_PIN_COLOR);

export type UseAddPinsToMapOptions = {
	/** Separate layer id when multiple features each manage their own pin layer. */
	layerId?: string;
	/** Pin fill colour — generates a teardrop SVG. Ignored when pinSvg is set. */
	pinColor?: string;
	/** Fully custom SVG data-URL. Overrides pinColor. */
	pinSvg?: string;
	pinWidth?: string;
	pinHeight?: string;
	/** Y-offset to align the pin tip on the coordinate (default: half pin height). */
	pinYOffset?: string;
};

const useAddPinsToMap = (options?: UseAddPinsToMapOptions) => {
	const { view } = useEsriMapContext();
	const layerId = options?.layerId ?? DEFAULT_DROP_PIN_LAYER_ID;
	// Drop-pin graphics layer — created when view is ready, torn down on cleanup
	const dropPinLayerRef = useRef<GraphicsLayer | null>(null);
	const pendingDropPinRef = useRef<[number, number][] | null>(null);

	const pinSvg =
		options?.pinSvg ??
		(options?.pinColor ? makeTeardropSvg(options.pinColor) : DEFAULT_PIN_SVG);
	const pinWidth = options?.pinWidth ?? '27px';
	const pinHeight = options?.pinHeight ?? '41px';
	const pinYOffset = options?.pinYOffset ?? '20.5px';

	const addPinGraphic = useCallback(
		(layer: GraphicsLayer, centers: [number, number][]): void => {
			layer.removeAll();
			layer.addMany(
				centers.map(
					center =>
						new Graphic({
							geometry: new Point({
								longitude: center[0],
								latitude: center[1],
							}),
							symbol: {
								type: 'picture-marker' as const,
								url: pinSvg,
								width: pinWidth,
								height: pinHeight,
								yoffset: pinYOffset,
							},
						}),
				),
			);
			// Asset layers may have been added after this pin layer was created,
			// which would draw them on top. Move the pin layer back to the top.
			const map = view?.map;
			if (map) {
				map.reorder(layer, map.layers.length - 1);
			}
		},
		[view, pinSvg, pinWidth, pinHeight, pinYOffset],
	);

	useEffect(() => {
		const esriMap = view?.map;
		if (!esriMap) return;
		const layer = new GraphicsLayer({ id: layerId });
		esriMap.add(layer);
		dropPinLayerRef.current = layer;
		// Pins may have been set before the layer existed (view not ready yet).
		const pending = pendingDropPinRef.current;
		if (pending?.length) {
			addPinGraphic(layer, pending);
			pendingDropPinRef.current = null;
		}
		return () => {
			esriMap.remove(layer);
			dropPinLayerRef.current = null;
		};
	}, [view, layerId, addPinGraphic]);

	const setDropPin = useCallback(
		(centers: [number, number][] | null): void => {
			if (!centers) {
				pendingDropPinRef.current = null;
				dropPinLayerRef.current?.removeAll();
				return;
			}
			const layer = dropPinLayerRef.current;
			if (!layer) {
				// Layer not ready yet — defer until view initialises
				pendingDropPinRef.current = centers;
				return;
			}
			addPinGraphic(layer, centers);
		},
		[addPinGraphic],
	);

	return setDropPin;
};

export default useAddPinsToMap;
